"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Clock, ShieldCheck, Zap, CalendarCheck, CircleCheck as CheckCircle2, MapPin, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const guarantees = [{
  icon: Clock,
  title: "30-45 Min Fast Arrival",
  detail: "Local Jonesboro & Clayton County dispatch"
}, {
  icon: ShieldCheck,
  title: "Licensed & Insured",
  detail: "Master plumbers standing by 24/7"
}, {
  icon: CheckCircle2,
  title: "Upfront Fair Pricing",
  detail: "No hidden fees or unexpected surge rates"
}];
export default function EmergencyBanner() {
  const shouldReduceMotion = useReducedMotion();
  const animationVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="emergencycalltoaction" data-nav="light" className="bg-background py-12 md:py-20 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={animationVariants} className="relative rounded-2xl bg-primary text-primary-foreground p-6 sm:p-10 lg:p-14 shadow-xl overflow-hidden border border-primary/20">
          {/* Subtle decorative background elements */}
          <div className="pointer-events-none absolute -right-20 -top-20 size-72 rounded-full bg-accent/15 blur-3xl" />
          <div className="pointer-events-none absolute -left-20 -bottom-20 size-72 rounded-full bg-chart-3/20 blur-3xl" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Content Area */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex flex-wrap items-center gap-2.5">
                <Badge variant="destructive" className="px-3 py-1 text-xs font-semibold tracking-wide uppercase flex items-center gap-1.5">
                  <AlertTriangle className="size-3.5 animate-pulse" />
                  24/7 Immediate Dispatch
                </Badge>
                <div className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-foreground/90 bg-primary-foreground/10 px-3 py-1 rounded-md">
                  <MapPin className="size-3.5 text-accent" />
                  <span>Serving Jonesboro, GA & Surrounding Areas</span>
                </div>
              </div>

              <div className="space-y-3">
                <h2 className="font-default text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-tight">
                  Plumbing Emergency Right Now? We Are Ready to Help.
                </h2>
                <p className="text-base sm:text-lg text-primary-foreground/85 max-w-2xl leading-relaxed">
                  Burst pipes, overflowing drains, or no hot water? Do not wait for water damage to spread. Call our local team for fast emergency repair.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 pt-2">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md text-base h-12 px-7 rounded-md justify-center">
                  <a href="tel:4708479076" className="inline-flex items-center gap-2.5">
                    <PhoneCall className="size-5" />
                    <span>Call Now: (470) 847-9076</span>
                  </a>
                </Button>

                <Button asChild variant="secondary" size="lg" className="font-medium text-base h-12 px-6 rounded-md justify-center">
                  <Link to="/contact/" className="inline-flex items-center gap-2">
                    <CalendarCheck className="size-5" />
                    <span>Request Online Service</span>
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Feature Panel */}
            <div className="lg:col-span-5">
              <div className="bg-primary-foreground/10 backdrop-blur-xs rounded-xl p-6 sm:p-8 border border-primary-foreground/15 space-y-4">
                <div className="flex items-center gap-2 text-accent font-semibold text-sm tracking-wide uppercase">
                  <Zap className="size-4" />
                  <span>Why Jonesboro Calls Us First</span>
                </div>

                <div className="space-y-4 pt-1">
                  {guarantees.map((item, index) => {
                  const Icon = item.icon;
                  return <div key={item.title} data-index={index} className="flex items-start gap-3.5">
                        <div className="p-2 rounded-md bg-primary-foreground/15 text-accent shrink-0 mt-0.5">
                          <Icon className="size-4" />
                        </div>
                        <div>
                          <h3 className="text-sm sm:text-base font-semibold text-primary-foreground leading-snug">
                            {item.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-primary-foreground/75 leading-normal">
                            {item.detail}
                          </p>
                        </div>
                      </div>;
                })}
                </div>

                <div className="pt-3 border-t border-primary-foreground/15 text-center sm:text-left">
                  <p className="text-xs text-primary-foreground/70">
                    Live dispatchers active now. Average phone response time under 60 seconds.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}